import { FC, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Avatar, Box, CircularProgress, Stack, Typography } from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import { useAuthStore } from '@/stores/authStore';
import getGravatarUrl from '@/helpers/getGravatarUrl';
import { useVideoCommentStore } from '@/stores/videoCommentStore';
import { CardWrapper } from './styles';
import CommentComposer from './CommentComposer';
import CommentItem from './CommentItem';
import { useNoteComments } from '../hooks/useNoteComments';

interface CommentsPanelProps {
  noteId: string;
  shareToken?: string | null;
  isOwner: boolean;
}

/** The custom video comment thread rendered in the note's right-hand panel. */
const CommentsPanel: FC<CommentsPanelProps> = ({ noteId, shareToken, isOwner }) => {
  const { currentUser, userData } = useAuthStore();
  const setHighlightedCommentId = useVideoCommentStore((s) => s.setHighlightedCommentId);
  const { data: comments, isLoading } = useNoteComments({ noteId, shareToken });
  const [searchParams] = useSearchParams();
  const didInitHighlight = useRef(false);

  // Deep-link highlight: emails link to ?comment=<id> (new) or ?thread=<id> (legacy/extension).
  useEffect(() => {
    if (didInitHighlight.current) return;
    const target = searchParams.get('comment') || searchParams.get('thread');
    if (target) {
      setHighlightedCommentId(target);
      didInitHighlight.current = true;
    }
  }, [searchParams, setHighlightedCommentId]);

  const totalComments = useMemo(
    () => (comments ?? []).reduce((sum, comment) => sum + 1 + comment.replies.length, 0),
    [comments],
  );

  return (
    <Stack spacing={1} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <CardWrapper
        sx={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          '@media (max-width: 600px)': { padding: '4px', margin: '0' },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          p={1}
          sx={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            '@media (max-width: 600px)': { padding: '4px', gap: '8px' },
          }}
        >
          <Avatar
            sx={{
              width: '40px',
              height: '40px',
              flexShrink: 0,
              '@media (max-width: 600px)': { width: '32px', height: '32px' },
            }}
            src={currentUser?.photoURL || getGravatarUrl(currentUser?.email || '')}
          >
            {currentUser?.displayName?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CommentComposer noteId={noteId} shareToken={shareToken} showTimestampToggle />
          </Box>
        </Stack>
      </CardWrapper>

      <CardWrapper
        sx={{
          p: 2,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          '@media (max-width: 600px)': { padding: '8px', margin: '0' },
        }}
      >
        <Typography variant="body1" sx={{ '@media (max-width: 600px)': { fontSize: '14px' } }}>
          {totalComments} {totalComments === 1 ? 'Comment' : 'Comments'}
        </Typography>
      </CardWrapper>

      <CardWrapper
        sx={{
          maxHeight: '500px',
          overflow: 'auto',
          minHeight: '350px',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          '@media (max-width: 600px)': { minHeight: '200px', maxHeight: '300px', padding: '4px', margin: '0' },
        }}
      >
        {isLoading ? (
          <Stack justifyContent="center" alignItems="center" sx={{ minHeight: '200px' }}>
            <CircularProgress />
          </Stack>
        ) : comments && comments.length > 0 ? (
          <Stack spacing={1.5} sx={{ p: 1 }}>
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                noteId={noteId}
                shareToken={shareToken}
                currentUserId={userData?.id}
                isNoteOwner={isOwner}
              />
            ))}
          </Stack>
        ) : (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: '250px', p: 2 }}>
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              No comments yet. Be the first to comment.
            </Typography>
          </Stack>
        )}
      </CardWrapper>
    </Stack>
  );
};

export default CommentsPanel;
